import { Checkbox, InputLabel, ListItemText, MenuItem, OutlinedInput, Select } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import ArticlesT2Row from './ArticlesT2Row';

const getArticleKey = (a) => a.code?.toString() || a.name;

const ArticlesT2TablePreview = ({
  newArticles,
  editedArticles,
  withOutChangesArticles,
  withIssuesArticles,
  newFormats,
  editedFormats,
  withOutChangesFormats,
  withIssuesFormats,
  selectedKeys,
  onToggle,
  onToggleAll,
  selectedFormatKeys,
  onToggleFormat,
  onToggleAllFormats,
  markedDuplicates,
  onMarkDuplicate,
}) => {
  const parentRef = useRef(null);
  const [articleExcelTypeFilter, setArticleExcelTypeFilter] = useState(['created', 'edited']);

  const formatsByArticle = useMemo(() => {
    const allFormats = [
      ...newFormats.map((f) => ({ ...f, type: 'created' })),
      ...editedFormats.map((f) => ({ ...f, type: 'edited' })),
      ...withOutChangesFormats.map((f) => ({ ...f, type: 'with-out-changes' })),
      ...withIssuesFormats.map((f) => ({ ...f, type: 'with-issues' })),
    ];
    const map = {};
    allFormats.forEach((f) => {
      if (!map[f.codeArticle]) map[f.codeArticle] = [];
      map[f.codeArticle].push(f);
    });
    return map;
  }, [newFormats, editedFormats, withOutChangesFormats, withIssuesFormats]);

  const filteredArticles = useMemo(() => {
    let result = [];
    if (articleExcelTypeFilter.includes('with-issues'))
      result = [...result, ...withIssuesArticles.map((a) => ({ ...a, type: 'with-issues' }))];
    if (articleExcelTypeFilter.includes('created'))
      result = [...result, ...newArticles.map((a) => ({ ...a, type: 'created' }))];
    if (articleExcelTypeFilter.includes('edited'))
      result = [...result, ...editedArticles.map((a) => ({ ...a, type: 'edited' }))];
    if (articleExcelTypeFilter.includes('with-out-changes'))
      result = [...result, ...withOutChangesArticles.map((a) => ({ ...a, type: 'with-out-changes' }))];
    return result;
  }, [newArticles, editedArticles, withOutChangesArticles, withIssuesArticles, articleExcelTypeFilter]);

  const selectableFilteredKeys = useMemo(
    () => filteredArticles.filter((a) => a.type !== 'with-issues' && a.type !== 'with-out-changes').map(getArticleKey),
    [filteredArticles],
  );

  const selectableFormatKeys = useMemo(() => {
    const keys = [];
    for (const article of filteredArticles) {
      const fmts = formatsByArticle[article.code] ?? [];
      for (const fmt of fmts) {
        if (fmt.type !== 'with-out-changes' && fmt.type !== 'with-issues' && fmt._excelRow != null) {
          keys.push(fmt._excelRow.toString());
        }
      }
    }
    return keys;
  }, [filteredArticles, formatsByArticle]);

  const allSelected =
    (selectableFilteredKeys.length > 0 || selectableFormatKeys.length > 0) &&
    selectableFilteredKeys.every((k) => selectedKeys?.has(k)) &&
    selectableFormatKeys.every((k) => selectedFormatKeys?.has(k));
  const someSelected =
    selectableFilteredKeys.some((k) => selectedKeys?.has(k)) ||
    selectableFormatKeys.some((k) => selectedFormatKeys?.has(k));

  const rowVirtualizer = useVirtualizer({
    count: filteredArticles.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    overscan: 5,
    measureElement: (el) => el?.getBoundingClientRect().height ?? 60,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? (virtualItems[0]?.start ?? 0) : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - (virtualItems.at(-1)?.end ?? 0) : 0;

  return (
    <>
      <div className="articles-table-filter-container">
        <div style={{ width: '20rem' }}>
          <InputLabel size="small" id="t2-filter-label">
            Tipo artículo
          </InputLabel>
          <br />
          <Select
            style={{ width: '20rem' }}
            size="small"
            labelId="t2-filter-label"
            multiple
            value={articleExcelTypeFilter}
            onChange={(e) => setArticleExcelTypeFilter(e.target.value)}
            input={<OutlinedInput />}
            renderValue={(selected) => {
              const map = {
                created: 'Por Crear',
                edited: 'Por editar',
                'with-out-changes': 'Sin cambios',
                'with-issues': 'Con problemas',
              };
              return selected.map((s) => map[s]).join(', ');
            }}
          >
            <MenuItem value="created">
              <Checkbox checked={articleExcelTypeFilter.includes('created')} />
              <ListItemText primary={`Por Crear (${newArticles.length})`} />
            </MenuItem>
            <MenuItem value="edited">
              <Checkbox checked={articleExcelTypeFilter.includes('edited')} />
              <ListItemText primary={`Por editar (${editedArticles.length})`} />
            </MenuItem>
            <MenuItem value="with-out-changes">
              <Checkbox checked={articleExcelTypeFilter.includes('with-out-changes')} />
              <ListItemText primary={`Sin cambios (${withOutChangesArticles.length})`} />
            </MenuItem>
            <MenuItem value="with-issues">
              <Checkbox checked={articleExcelTypeFilter.includes('with-issues')} />
              <ListItemText primary={`Con problemas (${withIssuesArticles.length})`} />
            </MenuItem>
          </Select>
        </div>
      </div>

      <div className="articles-table-major-container">
        <div className="articles-table-inner">
          <table className="table-preview table-articles-t2-preview table-header">
            <colgroup>
              <col style={{ width: 50 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 160 }} />
              <col style={{ width: 280 }} />
              <col style={{ width: 220 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 170 }} />
            </colgroup>
            <thead>
              {filteredArticles.length > 0 && (
                <tr>
                  <th>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(e) => {
                        onToggleAll?.(selectableFilteredKeys, e.target.checked);
                        onToggleAllFormats?.(selectableFormatKeys, e.target.checked);
                      }}
                    />
                  </th>
                  <th>CODIGO ARTICULO</th>
                  <th>IMAGEN PEQUEÑA</th>
                  <th>ARTES FINALES</th>
                  <th>NOMBRE</th>
                  <th>SIMILARES</th>
                  <th>HIPER</th>
                  <th>PROXIMIDAD</th>
                  <th>ULTRA</th>
                  <th>MONTAJE</th>
                  <th>ACTIVO</th>
                  <th>CANTIDAD</th>
                  <th>PALABRAS CLAVES</th>
                  <th>CODIGO FORMATO</th>
                  <th>FORMATO</th>
                  <th>MATERIAL</th>
                </tr>
              )}
            </thead>
          </table>

          <div className="table-content-articles-container" ref={parentRef} style={{ height: '50vh' }}>
            <table className="table-preview table-articles-t2-preview">
              <colgroup>
                <col style={{ width: 50 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 280 }} />
                <col style={{ width: 220 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 200 }} />
                <col style={{ width: 170 }} />
                <col style={{ width: 170 }} />
                <col style={{ width: 170 }} />
              </colgroup>
              {filteredArticles.length > 0 ? (
                <>
                  {paddingTop > 0 && (
                    <tbody>
                      <tr style={{ height: paddingTop }}><td colSpan={16} /></tr>
                    </tbody>
                  )}
                  {virtualItems.map((virtualRow) => {
                    const article = filteredArticles[virtualRow.index];
                    const articleKey = getArticleKey(article);
                    return (
                      <ArticlesT2Row
                        key={`${articleKey}-${virtualRow.index}`}
                        ref={(el) => { if (el) rowVirtualizer.measureElement(el); }}
                        data-index={virtualRow.index}
                        article={article}
                        formats={formatsByArticle[article.code] ?? []}
                        isSelected={selectedKeys?.has(articleKey)}
                        onToggle={() => onToggle?.(articleKey)}
                        isDisabled={article.type === 'with-issues'}
                        selectedFormatKeys={selectedFormatKeys}
                        onToggleFormat={onToggleFormat}
                        onToggleAllFormats={onToggleAllFormats}
                        markedDuplicateCode={markedDuplicates?.[articleKey]}
                        onMarkDuplicate={(similarCode) => onMarkDuplicate?.(articleKey, similarCode)}
                      />
                    );
                  })}
                  {paddingBottom > 0 && (
                    <tbody>
                      <tr style={{ height: paddingBottom }}><td colSpan={16} /></tr>
                    </tbody>
                  )}
                </>
              ) : (
                <tbody style={{ height: '10rem' }}>
                  <tr style={{ height: '10rem' }}>
                    <td>
                      <span
                        style={{
                          color: '#6e6b6b',
                          fontSize: '1.2rem',
                          fontStyle: 'italic',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '1rem 2rem',
                        }}
                      >
                        No hay artículos para mostrar
                      </span>
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticlesT2TablePreview;
